/**
 * Real-time pending mediations counter for admins/super-admins.
 * - Fetches initial count from REST
 * - Subscribes to `mediation_pending_changed` socket event (admins room)
 * - Polls every 60s as a safety net
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { mediationsApi } from './api';

export default function useMediationsCount(user, eligible) {
  const [count, setCount] = useState(0);
  const socketRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await mediationsApi.count();
      setCount(res.data?.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user || !eligible) {
      setCount(0);
      return;
    }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await mediationsApi.count();
        if (!cancelled) setCount(res.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchOnce();

    const interval = setInterval(fetchOnce, 60000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
    });
    socket.on('mediation_pending_changed', () => {
      if (!cancelled) fetchOnce();
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      try { socket.disconnect(); } catch { /* ignore */ }
    };
  }, [user, eligible]);

  return { count, refresh };
}
